import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, ChevronRight, Search, KeyRound, Shield, Download, FileSpreadsheet, AlertCircle, CheckCircle2, Pencil } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { API, auth as authLib } from "@/lib/api";
import ReportingManagerCombobox from "@/components/ReportingManagerCombobox";

const empty = {
  emp_id: "", name: "", email: "", reporting_manager: "",
  reporting_manager_emp_id: "",
  department_id: "", sub_department_id: "", category_id: "", location_id: "",
  status: "Active", dob: "", phone: "", designation: "",
};

export default function Employees() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [masters, setMasters] = useState({ depts: [], subs: [], cats: [], locs: [] });
  const [roleDialog, setRoleDialog] = useState(null);
  const [pwdDialog, setPwdDialog] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [overwrite, setOverwrite] = useState(false);
  const [rolesList, setRolesList] = useState([]);
  const roleMap = Object.fromEntries(rolesList.map((r) => [r.key, r.name]));

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQ(q); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [q]);

  async function loadMasters() {
    const [depts, subs, cats, locs, rls] = await Promise.all([
      api.get("/masters/departments"),
      api.get("/masters/sub-departments"),
      api.get("/masters/categories"),
      api.get("/masters/locations"),
      api.get("/roles"),
    ]);
    setMasters({ depts: depts.data, subs: subs.data, cats: cats.data, locs: locs.data });
    setRolesList(rls.data || []);
  }

  async function load() {
    setLoading(true);
    try {
      const params = { page, limit };
      if (debouncedQ) params.search = debouncedQ;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/employees", { params });
      setItems(data.data || []);
      setTotal(data.total || 0);
    } finally { setLoading(false); }
  }
  useEffect(() => { loadMasters(); }, []);
  useEffect(() => { load(); }, [page, debouncedQ, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save() {
    try {
      if (editing) {
        await api.put(`/employees/${editing.id}`, form);
        toast.success("Employee updated");
      } else {
        await api.post("/employees", form);
        toast.success("Employee added");
      }
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  }
  function openEdit(e) {
    setEditing(e);
    setForm({
      emp_id: e.emp_id, name: e.name, email: e.email || "",
      reporting_manager: e.reporting_manager || "",
      reporting_manager_emp_id: e.reporting_manager_emp_id || "",
      department_id: e.department_id || "",
      sub_department_id: e.sub_department_id || "",
      category_id: e.category_id || "",
      location_id: e.location_id || "",
      status: e.status || "Active",
      dob: e.dob || "",
      phone: e.phone || "",
      designation: e.designation || "",
    });
    setOpen(true);
  }
  async function toggleStatus(e) {
    const next = e.status === "Active" ? "Inactive" : "Active";
    await api.post(`/employees/${e.id}/status`, { status: next });
    toast.success(`${e.emp_id} → ${next}`);
    load();
  }

  const filtered = items;

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const pageStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const pageEnd = Math.min(total, page * limit);

  const find = (arr, id) => arr.find(x => x.id === id)?.name || "—";

  return (
    <div className="p-6 lg:p-10 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-sky-600 font-bold">Master</div>
          <h1 className="font-display font-black text-3xl lg:text-4xl text-slate-900 mt-1">Employees</h1>
          <p className="text-slate-500 mt-1">Verified employee directory — used for quiz access & scoring.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-xl" onClick={async () => {
            try {
              const r = await fetch(`${API}/employees/export.xlsx`, { headers: { Authorization: `Bearer ${authLib.token()}` } });
              if (!r.ok) throw new Error("Failed");
              const blob = await r.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url; a.download = "employees-export.xlsx"; a.click();
              URL.revokeObjectURL(url);
              toast.success("Employee export downloaded");
            } catch {
              toast.error("Failed to export");
            }
          }} data-testid="export-employees-btn">
            <Download className="h-4 w-4 mr-1.5"/>Export to Excel
          </Button>
          <Button variant="outline" className="rounded-xl" onClick={()=>{ setImportOpen(true); setImportResult(null); }} data-testid="import-employees-btn">
            <FileSpreadsheet className="h-4 w-4 mr-1.5"/>Import from Excel
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-employee-btn" className="bg-sky-500 hover:bg-sky-600 rounded-xl">
                <Plus className="h-4 w-4 mr-1.5"/>Add Employee
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>Add New Employee</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Employee ID*"><Input data-testid="emp-id-input" value={form.emp_id} onChange={e=>setForm({...form, emp_id:e.target.value})} disabled={!!editing}/></Field>
              <Field label="Full Name*"><Input data-testid="emp-name-input" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/></Field>
              <Field label="Email"><Input data-testid="emp-email-input" type="text" value={form.email} onChange={e=>setForm({...form, email:e.target.value})}/></Field>
              <Field label="Date of Birth"><Input data-testid="emp-dob-input" type="date" value={form.dob || ""} onChange={e=>setForm({...form, dob:e.target.value})}/></Field>
              <Field label="Phone (+91…)"><Input data-testid="emp-phone-input" type="tel" value={form.phone || ""} onChange={e=>setForm({...form, phone:e.target.value})} placeholder="+919999999999"/></Field>
              <Field label="Designation"><Input data-testid="emp-desig-input" value={form.designation || ""} onChange={e=>setForm({...form, designation:e.target.value})} placeholder="Senior Staff Nurse"/></Field>
              <Field label="Reporting Manager (name)"><Input data-testid="emp-rm-name-input" value={form.reporting_manager} onChange={e=>setForm({...form, reporting_manager:e.target.value})} placeholder="Free text — display only"/></Field>
              <Field label="Reporting Manager (Emp ID)">
                <ReportingManagerCombobox
                  value={form.reporting_manager_emp_id}
                  onChange={(empId, name) => setForm({
                    ...form,
                    reporting_manager_emp_id: empId,
                    // Auto-fill the display name when picking from the list (if user hasn't customized it)
                    reporting_manager: name || form.reporting_manager,
                  })}
                  excludeEmpId={form.emp_id}
                />
              </Field>
              <Field label="Department">
                <SelectField val={form.department_id} onChange={v=>setForm({...form, department_id:v})} options={masters.depts}/>
              </Field>
              <Field label="Sub-Department">
                <SelectField val={form.sub_department_id} onChange={v=>setForm({...form, sub_department_id:v})}
                  options={masters.subs.filter(s => !form.department_id || s.parent_id === form.department_id)} />
              </Field>
              <Field label="Category">
                <SelectField val={form.category_id} onChange={v=>setForm({...form, category_id:v})} options={masters.cats}/>
              </Field>
              <Field label="Location">
                <SelectField val={form.location_id} onChange={v=>setForm({...form, location_id:v})} options={masters.locs}/>
              </Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={v=>setForm({...form, status:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button onClick={save} data-testid="emp-save-btn" className="bg-sky-500 hover:bg-sky-600">{editing ? "Update Employee" : "Save Employee"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </header>

      <Card className="border-slate-200 rounded-2xl">
        <CardContent className="p-0">
          <div className="p-4 border-b border-slate-100 flex items-center gap-3 flex-wrap">
            <Search className="h-4 w-4 text-slate-400"/>
            <Input placeholder="Search by name, ID, email…" value={q} onChange={e=>setQ(e.target.value)}
              data-testid="employee-search" className="border-0 focus-visible:ring-0 shadow-none px-0 flex-1 min-w-[200px]"/>
            <Select value={statusFilter} onValueChange={(v)=>{ setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="h-8 w-[130px] rounded-lg text-xs" data-testid="emp-status-filter"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Badge variant="secondary" className="bg-slate-100 text-slate-600" data-testid="emp-total-badge">
              {loading ? "Loading…" : `${total} employees`}
            </Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="py-3 px-4">Emp ID</th>
                  <th className="py-3 px-4">Name & Email</th>
                  <th className="py-3 px-4">Dept / SubDept</th>
                  <th className="py-3 px-4">Category</th>
                  <th className="py-3 px-4">Role</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-slate-400">No employees yet.</td></tr>
                )}
                {filtered.map(e => (
                  <tr key={e.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                    <td className="py-3 px-4 font-mono font-bold text-sky-700">{e.emp_id}</td>
                    <td className="py-3 px-4">
                      <div className="font-medium text-slate-900">{e.name}</div>
                      <div className="text-xs text-slate-400">{e.email}</div>
                    </td>
                    <td className="py-3 px-4">{find(masters.depts, e.department_id)} <span className="text-slate-300">/</span> {find(masters.subs, e.sub_department_id)}</td>
                    <td className="py-3 px-4">{find(masters.cats, e.category_id)}</td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 rounded-full text-xs font-bold bg-sky-50 text-sky-700">{roleMap[e.role || "employee"] || e.role || "Employee"}</span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={e.status === "Active"}
                          onCheckedChange={() => toggleStatus(e)}
                          data-testid={`status-toggle-${e.emp_id}`}
                        />
                        <span className={`text-xs font-bold ${e.status === "Active" ? "text-green-700" : "text-slate-400"}`}>{e.status}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={()=>openEdit(e)} title="Edit" data-testid={`edit-btn-${e.emp_id}`}><Pencil className="h-4 w-4 text-slate-600"/></Button>
                        <Link to={`/employees/${e.id}`}>
                          <Button variant="ghost" size="sm" data-testid={`view-scoreboard-${e.emp_id}`}><ChevronRight className="h-4 w-4"/></Button>
                        </Link>
                        <Button variant="ghost" size="sm" onClick={()=>setRoleDialog(e)} title="Set role" data-testid={`role-btn-${e.emp_id}`}><Shield className="h-4 w-4 text-sky-600"/></Button>
                        <Button variant="ghost" size="sm" onClick={()=>{ setPwdDialog(e); setNewPwd(""); }} title="Reset password" data-testid={`pwd-btn-${e.emp_id}`}><KeyRound className="h-4 w-4 text-amber-600"/></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
            <div data-testid="emp-page-info">
              {total === 0 ? "0 of 0" : `${pageStart}–${pageEnd} of ${total}`}
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="h-8 rounded-lg" disabled={page <= 1 || loading}
                onClick={()=>setPage(p=>Math.max(1, p-1))} data-testid="emp-prev-page">Prev</Button>
              <span className="px-2 font-mono">{page} / {totalPages}</span>
              <Button variant="outline" size="sm" className="h-8 rounded-lg" disabled={page >= totalPages || loading}
                onClick={()=>setPage(p=>Math.min(totalPages, p+1))} data-testid="emp-next-page">Next</Button>
            </div>
          </div>
        </CardContent>
      </Card>
      <Dialog open={importOpen} onOpenChange={(o)=>{ setImportOpen(o); if(!o) setImportResult(null); }}>
        <DialogContent className="max-w-5xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-emerald-600"/>Bulk Import Employees</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[80vh] overflow-y-auto pr-1">
            <div className="flex items-center justify-between p-3 rounded-xl bg-sky-50 border border-sky-200">
              <div className="text-sm">
                <div className="font-bold text-sky-900">Step 1 — Download the template</div>
                <div className="text-xs text-sky-700/80">Includes DOB column (DD/MM/YYYY). Fill in your data, save as .xlsx.</div>
              </div>
              <Button variant="outline" size="sm" onClick={async()=>{
                const resp = await fetch(`${API}/employees/import-template`, { headers: { Authorization: `Bearer ${authLib.token()}` }});
                const blob = await resp.blob();
                const url = URL.createObjectURL(blob); const a = document.createElement("a");
                a.href = url; a.download = "employee-import-template.xlsx"; a.click(); URL.revokeObjectURL(url);
              }} data-testid="download-template-btn"><Download className="h-4 w-4 mr-1.5"/>Template</Button>
            </div>

            <div className="p-3 rounded-xl bg-amber-50 border border-amber-200" data-testid="overwrite-toggle-wrap">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={overwrite} onChange={(e)=>setOverwrite(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-amber-600" data-testid="overwrite-toggle"/>
                <div>
                  <div className="font-bold text-sm text-amber-900">Overwrite existing employees with same Employee ID</div>
                  <div className="text-xs text-amber-700/80 mt-0.5">When ON, any row whose Employee ID already exists in the system is <span className="font-bold">updated</span> with the new values instead of being rejected.</div>
                </div>
              </label>
            </div>

            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
              <div className="font-bold text-sm">Step 2 — Upload your .xlsx</div>
              <div className="text-xs text-slate-500 mt-0.5 mb-3">Department / Sub-Department / Category / Location must match master names (case-insensitive). Rows with missing or invalid data appear in the fix-up table below — edit them inline and re-submit.</div>
              <input type="file" accept=".xlsx" data-testid="import-file-input" onChange={async (e)=>{
                const f = e.target.files?.[0]; if (!f) return;
                setImporting(true);
                try {
                  const fd = new FormData(); fd.append("file", f);
                  const r = await api.post(`/employees/bulk-import?overwrite=${overwrite ? 1 : 0}`, fd, { headers: { "Content-Type": "multipart/form-data" }});
                  setImportResult(r.data);
                  const u = r.data.updated_count || 0;
                  toast.success(`Imported ${r.data.created_count}${u ? `, updated ${u}` : ""}; need fix-up ${r.data.rejected_count}`);
                  load();
                } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
                finally { setImporting(false); e.target.value = ""; }
              }}/>
              {importing && <div className="text-sm text-slate-500 mt-2">Importing…</div>}
            </div>

            {importResult && (
              <div className="space-y-3" data-testid="import-result">
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 rounded-xl bg-green-50 border border-green-200">
                    <div className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-green-600"/><div className="font-display font-black text-2xl text-green-700">{importResult.created_count}</div></div>
                    <div className="text-xs uppercase tracking-wider text-green-700/80 mt-0.5 font-bold">Created</div>
                  </div>
                  <div className="p-3 rounded-xl bg-blue-50 border border-blue-200">
                    <div className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-blue-600"/><div className="font-display font-black text-2xl text-blue-700">{importResult.updated_count || 0}</div></div>
                    <div className="text-xs uppercase tracking-wider text-blue-700/80 mt-0.5 font-bold">Updated</div>
                  </div>
                  <div className="p-3 rounded-xl bg-amber-50 border border-amber-200">
                    <div className="flex items-center gap-2"><AlertCircle className="h-5 w-5 text-amber-600"/><div className="font-display font-black text-2xl text-amber-700">{importResult.rejected_count}</div></div>
                    <div className="text-xs uppercase tracking-wider text-amber-700/80 mt-0.5 font-bold">Need Fix-up</div>
                  </div>
                </div>
                {importResult.rejected_count > 0 && (
                  <FixupTable
                    rejected={importResult.rejected}
                    overwriteDefault={overwrite}
                    onResubmit={async (rows, doOverwrite) => {
                      try {
                        const r = await api.post("/employees/bulk-fix", { rows, overwrite: doOverwrite });
                        setImportResult(r.data);
                        const u = r.data.updated_count || 0;
                        toast.success(`Saved ${r.data.created_count}${u ? `, updated ${u}` : ""}; still need fix-up ${r.data.rejected_count}`);
                        load();
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Fix-up failed");
                      }
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!roleDialog} onOpenChange={(o)=>!o && setRoleDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Set Role · {roleDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {rolesList.map(r => (
              <button key={r.key} onClick={async () => {
                await api.put(`/employees/${roleDialog.id}/role`, { role: r.key });
                toast.success(`Role set to ${r.name}`);
                setRoleDialog(null); load();
              }} data-testid={`set-role-${r.key}`}
                className={`w-full text-left p-3 rounded-xl border-2 transition-colors ${roleDialog?.role === r.key ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <div className="font-bold">{r.name}</div>
                <div className="text-xs text-slate-500">{r.description || `Role key: ${r.key}`}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwdDialog} onOpenChange={(o)=>!o && setPwdDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset Password · {pwdDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="New Password">
              <Input type="text" value={newPwd} onChange={e=>setNewPwd(e.target.value)} placeholder="At least 6 characters" data-testid="new-pwd-input"/>
            </Field>
            <div className="text-xs text-slate-500">Employee can login at <span className="font-mono font-bold">/employee/login</span> with their Employee ID and this password.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setPwdDialog(null)}>Cancel</Button>
            <Button onClick={async () => {
              if (newPwd.length < 4) { toast.error("Password too short"); return; }
              await api.post(`/employees/${pwdDialog.id}/reset-password`, { new_password: newPwd });
              toast.success("Password updated");
              setPwdDialog(null);
            }} className="bg-sky-500 hover:bg-sky-600" data-testid="save-pwd">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
      {children}
    </div>
  );
}
function SelectField({ val, onChange, options }) {
  return (
    <Select value={val || undefined} onValueChange={onChange}>
      <SelectTrigger><SelectValue placeholder="Select…"/></SelectTrigger>
      <SelectContent>
        {options.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

const FIXUP_COLS = [
  { key: "emp_id", label: "Emp ID", w: 110, required: true },
  { key: "name", label: "Name", w: 160, required: true },
  { key: "email", label: "Email", w: 200 },
  { key: "phone", label: "Phone", w: 140 },
  { key: "dob", label: "DOB (DD/MM/YYYY)", w: 150 },
  { key: "department", label: "Department", w: 140 },
  { key: "sub_department", label: "Sub Dept", w: 140 },
  { key: "category", label: "Category", w: 120 },
  { key: "reporting_manager", label: "Reporting Mgr", w: 140 },
  { key: "status", label: "Status", w: 90 },
  { key: "location", label: "Location", w: 160 },
];

function FixupTable({ rejected, onResubmit, overwriteDefault = false }) {
  const [rows, setRows] = useState(() => rejected.map((r) => ({
    _row: r.row,
    _reason: r.reason,
    _missing: r.missing || [],
    ...(r.values || {}),
  })));
  const [saving, setSaving] = useState(false);
  const [overwrite, setOverwrite] = useState(overwriteDefault);

  // Sync if a new import result comes in
  useEffect(() => {
    setRows(rejected.map((r) => ({
      _row: r.row, _reason: r.reason, _missing: r.missing || [], ...(r.values || {}),
    })));
  }, [rejected]);
  useEffect(() => { setOverwrite(overwriteDefault); }, [overwriteDefault]);

  function updateCell(idx, key, val) {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: val };
      return next;
    });
  }

  function removeRow(idx) {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  }

  async function submitFixed() {
    setSaving(true);
    try {
      await onResubmit(rows, overwrite);
    } finally {
      setSaving(false);
    }
  }

  if (!rows.length) {
    return <div className="text-sm text-emerald-700 p-3 bg-emerald-50 rounded-xl border border-emerald-200">All rows imported successfully.</div>;
  }

  return (
    <div className="space-y-2" data-testid="fixup-table">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm">
          <span className="font-bold text-amber-700">Fix-up the rows below</span>
          <span className="text-slate-500"> — required cells are highlighted, then click "Save fixed rows".</span>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs font-bold text-amber-700 cursor-pointer" data-testid="fixup-overwrite-wrap">
            <input type="checkbox" checked={overwrite} onChange={(e)=>setOverwrite(e.target.checked)}
              className="h-3.5 w-3.5 accent-amber-600" data-testid="fixup-overwrite-toggle"/>
            Overwrite duplicates
          </label>
          <Button onClick={submitFixed} disabled={saving} size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="fixup-save-btn">
            {saving ? "Saving…" : `Save fixed rows (${rows.length})`}
          </Button>
        </div>
      </div>
      <div className="border border-slate-200 rounded-xl overflow-auto max-h-[420px]">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-left text-slate-500 sticky top-0">
            <tr>
              <th className="px-2 py-2 w-[40px]">Row</th>
              {FIXUP_COLS.map((c) => (
                <th key={c.key} className="px-2 py-2" style={{ minWidth: c.w }}>
                  {c.label}{c.required && <span className="text-red-500"> *</span>}
                </th>
              ))}
              <th className="px-2 py-2 w-[40px]"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100 align-top">
                <td className="px-2 py-1 font-mono text-slate-400">{r._row || "—"}</td>
                {FIXUP_COLS.map((c) => {
                  const isMissing = (r._missing || []).includes(c.key) || (c.required && !r[c.key]);
                  return (
                    <td key={c.key} className="px-1 py-1">
                      <Input
                        value={r[c.key] || ""}
                        onChange={(e) => updateCell(i, c.key, e.target.value)}
                        className={`h-8 text-xs rounded-md ${isMissing ? "border-amber-400 bg-amber-50" : ""}`}
                        placeholder={c.key === "dob" ? "DD/MM/YYYY" : ""}
                        data-testid={`fixup-${i}-${c.key}`}
                      />
                    </td>
                  );
                })}
                <td className="px-1 py-1">
                  <Button variant="ghost" size="sm" onClick={() => removeRow(i)} title="Skip this row"
                    className="h-7 w-7 p-0 text-slate-400 hover:text-red-600" data-testid={`fixup-skip-${i}`}>×</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-slate-400 flex flex-wrap gap-3">
        <span>{rows.filter((r) => (r._missing || []).length || !r.emp_id || !r.name).length} row(s) still need attention</span>
      </div>
    </div>
  );
}
